"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Loader2 } from "lucide-react";

import { BrandLogo } from "@/components/brand/BrandLogo";

type GlobalLoadingContextValue = {
  pendingCount: number;
  isActive: boolean;
  startLoading: () => void;
  stopLoading: () => void;
};

type FetchSubscriber = {
  start: () => void;
  stop: () => void;
};

type FetchPatchState = {
  originalFetch: typeof fetch;
  subscribers: Set<FetchSubscriber>;
};

const GlobalLoadingContext = createContext<GlobalLoadingContextValue | undefined>(undefined);

const IGNORED_FETCH_PATHS = [
  "/_next/static",
  "/_next/image",
  "/_next/webpack-hmr",
  "/_next/font",
  "/_next/data",
];

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return "";
}

function readHeader(headers: HeadersInit | undefined, headerName: string): string | null {
  if (!headers) {
    return null;
  }

  if (headers instanceof Headers) {
    return headers.get(headerName);
  }

  if (Array.isArray(headers)) {
    const match = headers.find(([name]) => name.toLowerCase() === headerName.toLowerCase());
    return match?.[1] ?? null;
  }

  const record = headers as Record<string, string>;
  const key = Object.keys(record).find((name) => name.toLowerCase() === headerName.toLowerCase());
  return key ? record[key] : null;
}

function getRequestHeader(input: RequestInfo | URL, init: RequestInit | undefined, name: string) {
  const fromInit = readHeader(init?.headers, name);
  if (fromInit) {
    return fromInit;
  }
  if (input instanceof Request) {
    return input.headers.get(name);
  }
  return null;
}

function shouldTrackRequest(input: RequestInfo | URL, init?: RequestInit): boolean {
  const url = getRequestUrl(input);
  if (IGNORED_FETCH_PATHS.some((segment) => url.includes(segment))) {
    return false;
  }

  const purposeHeader =
    getRequestHeader(input, init, "purpose") ?? getRequestHeader(input, init, "sec-purpose");
  if (purposeHeader?.toLowerCase().includes("prefetch")) {
    return false;
  }

  if (getRequestHeader(input, init, "next-router-prefetch")) {
    return false;
  }

  if (
    getRequestHeader(input, init, "next-action") ||
    getRequestHeader(input, init, "x-next-action") ||
    getRequestHeader(input, init, "x-action")
  ) {
    return false;
  }

  return true;
}

function getFetchPatchState(): FetchPatchState | null {
  if (typeof window === "undefined") return null;
  const maybeState = (window as Window & { __viviFetchPatch?: FetchPatchState }).__viviFetchPatch;
  return maybeState ?? null;
}

function setFetchPatchState(state: FetchPatchState | null) {
  if (typeof window === "undefined") return;
  const target = window as Window & { __viviFetchPatch?: FetchPatchState };
  if (state) {
    target.__viviFetchPatch = state;
  } else {
    delete target.__viviFetchPatch;
  }
}

interface GlobalLoadingProviderProps {
  children: ReactNode;
}

export function GlobalLoadingProvider({ children }: GlobalLoadingProviderProps) {
  const [pendingCount, setPendingCount] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigationTimerRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const pendingDeltaRef = useRef(0);
  const flushScheduledRef = useRef(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locationKey = `${pathname}?${searchParams?.toString() ?? ""}`;
  const lastLocationKeyRef = useRef(locationKey);

  const flushPendingDelta = useCallback(() => {
    flushScheduledRef.current = false;
    const delta = pendingDeltaRef.current;
    if (delta === 0) {
      return;
    }
    pendingDeltaRef.current = 0;
    if (!isMountedRef.current) {
      return;
    }
    setPendingCount((current) => Math.max(0, current + delta));
  }, []);

  const scheduleFlushPendingDelta = useCallback(() => {
    if (flushScheduledRef.current) {
      return;
    }
    flushScheduledRef.current = true;

    if (typeof queueMicrotask === "function") {
      queueMicrotask(flushPendingDelta);
      return;
    }

    Promise.resolve().then(flushPendingDelta);
  }, [flushPendingDelta]);

  const startLoading = useCallback(() => {
    pendingDeltaRef.current += 1;
    scheduleFlushPendingDelta();
  }, [scheduleFlushPendingDelta]);

  const stopLoading = useCallback(() => {
    pendingDeltaRef.current -= 1;
    scheduleFlushPendingDelta();
  }, [scheduleFlushPendingDelta]);

  const value = useMemo(
    () => ({
      pendingCount,
      isActive: pendingCount > 0 || isNavigating,
      startLoading,
      stopLoading,
    }),
    [pendingCount, isNavigating, startLoading, stopLoading],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.fetch !== "function") {
      return;
    }

    const subscriber: FetchSubscriber = {
      start: startLoading,
      stop: stopLoading,
    };

    const existing = getFetchPatchState();
    if (existing) {
      existing.subscribers.add(subscriber);
      return () => {
        existing.subscribers.delete(subscriber);
      };
    }

    const originalFetch = window.fetch.bind(window);
    const subscribers = new Set<FetchSubscriber>([subscriber]);

    const patchedFetch: typeof fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const track = shouldTrackRequest(input, init);
      if (track) {
        subscribers.forEach((handler) => handler.start());
      }
      try {
        return await originalFetch(input, init);
      } finally {
        if (track) {
          subscribers.forEach((handler) => handler.stop());
        }
      }
    };

    window.fetch = patchedFetch;
    setFetchPatchState({ originalFetch, subscribers });

    return () => {
      subscribers.delete(subscriber);
      if (subscribers.size === 0) {
        window.fetch = originalFetch;
        setFetchPatchState(null);
      }
    };
  }, [startLoading, stopLoading]);

  useEffect(() => {
    if (lastLocationKeyRef.current !== locationKey) {
      lastLocationKeyRef.current = locationKey;
      setIsNavigating(false);
      if (navigationTimerRef.current != null) {
        window.clearTimeout(navigationTimerRef.current);
        navigationTimerRef.current = null;
      }
    }
  }, [locationKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStart = () => {
      setIsNavigating(true);
      if (navigationTimerRef.current != null) {
        window.clearTimeout(navigationTimerRef.current);
      }
      navigationTimerRef.current = window.setTimeout(() => {
        setIsNavigating(false);
        navigationTimerRef.current = null;
      }, 8000);
    };

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a");
      if (!anchor) return;
      if (anchor.getAttribute("target") && anchor.getAttribute("target") !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      if (anchor.getAttribute("data-global-loader") === "false") return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("mailto:") || href.startsWith("tel:")) return;

      const nextUrl = new URL(href, window.location.href);
      if (nextUrl.origin !== window.location.origin) return;
      const currentUrl = new URL(window.location.href);
      if (nextUrl.pathname === currentUrl.pathname && nextUrl.search === currentUrl.search) {
        return;
      }
      handleStart();
    };

    const handlePopState = () => handleStart();

    document.addEventListener("click", handleClick, true);
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
      {value.isActive ? (
        <div
          aria-live="polite"
          aria-busy
          className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-neutral-950/50 backdrop-blur-md" />
          <div className="relative flex w-[240px] flex-col items-center gap-3 rounded-3xl border border-primary/30 bg-white/95 px-6 py-6 text-center text-neutral-900 shadow-[0_30px_70px_-38px_rgba(1,167,157,0.6)] dark:border-primary/40 dark:bg-neutral-900/90 dark:text-white">
            <BrandLogo variant="dark" layoutVariant="vertical" showText={false} />
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
              Cargando...
            </div>
          </div>
        </div>
      ) : null}
    </GlobalLoadingContext.Provider>
  );
}

export function useGlobalLoading() {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error("useGlobalLoading must be used within a GlobalLoadingProvider");
  }
  return context;
}
